import { Client, fetchExchange, OperationResult } from "@urql/core";
import { authExchange } from "@urql/exchange-auth";
import { getIdToken } from "user-management/src/cognito/kahunUserAuth";
import {
  AskPromptInSessionMutationVariables,
  ChatReply,
  DuplicateResult,
  Exact,
  ExchangeFeedback,
  ExchangeFeedbackInput,
  MutationAskQuestionInSessionArgs,
  PredefinedInsight,
  PromptReply,
  QueryStartChatArgs,
  QuestionOptions,
  Result,
  Session,
  SessionByCaseIdReply,
  SessionItem,
  SessionStartReply,
  StartReply,
} from "./types/graphql";
import {
  askQuestionInSession,
  duplicateSession,
  GetInitInfo,
  getSessionsByUserQuery,
  getSessionsQuery,
  removeSession,
  restoreAnySession,
  restoreSession,
  StartChatQuery,
  startSession,
  startSessionByCaseIdWithCheck,
  startSessionByCaseIdNoCheck,
  updateExchangeFeedback,
  userTakeover,
  answerQuestion,
  answerPrompt,
  askPromptInSession,
  updatePromptFeedback,
} from "./queries";

const LOCAL_URL = "http://localhost:3016/";
const DEV_URL =
  "https://e6jx63sg6mqz5sgw4mpd57khvy0jreju.lambda-url.us-east-1.on.aws/";
const PROD_URL =
  "https://bf5i7ox6qgmpgygkfs74bovbp40knpfa.lambda-url.us-east-1.on.aws/";

function getEndpoint() {
  if (process.env.REACT_APP_PROD_ENDPOINT === "true") {
    return PROD_URL;
  }

  const host = window.location.hostname;

  const hash = window.location.hash?.slice(1);
  const hostFromHash = hash?.startsWith("host:")
    ? `https://${hash.slice("host:".length)}.lambda-url.us-east-1.on.aws/`
    : null;
  if (hostFromHash) console.log("hostFromHash", hostFromHash);

  switch (host) {
    case "localhost":
      console.log("localhost", process.env.REACT_APP_NODE_ENV);

      if (hostFromHash) return hostFromHash;

      if (process.env.REACT_APP_ENDPOINT) return process.env.REACT_APP_ENDPOINT;

      switch (process.env.REACT_APP_NODE_ENV) {
        case "development":
          return DEV_URL;
        case "production":
          return PROD_URL;
        default:
          return LOCAL_URL;
      }
    case "dialogue-dev.kahun.com":
    case "develop.kahun.com":
      if (hostFromHash) return hostFromHash;
      return DEV_URL;
    case "dialogue.kahun.com":
    case "diagnose.kahun.com":
      return PROD_URL;
    default:
      return DEV_URL;
  }
}

export class RagDao {
  public client?: Client;
  constructor() {
    const getFetchOptions = () =>
      this.appHeader ? { headers: { kahun_rag_app: this.appHeader } } : {};
    this.client = new Client({
      url: getEndpoint(),
      fetchOptions: getFetchOptions,
      exchanges: [
        authExchange(async (utils) => {
          //Use Id token, so we can use the email as a key to the sessions
          let accessToken = await getIdToken();
          return {
            addAuthToOperation(operation) {
              return utils.appendHeaders(operation, {
                Authorization: `Bearer ${accessToken}`,
              });
            },
            async refreshAuth() {
              accessToken = await getIdToken();
            },
            didAuthError(error, _operation) {
              return error?.graphQLErrors?.length > 0;
              //return error.graphQLErrors.some(
              //   (e) => e.extensions?.code === "FORBIDDEN",
              // );
            },
            willAuthError(operation) {
              if (!accessToken) return true;
              //check if the token is about to expire
              const payload = accessToken.split(".")[1];
              const decodedPayload = JSON.parse(atob(payload).toString());
              const currentTimestamp = Math.floor(Date.now() / 1000);
              return decodedPayload?.exp + 60 < currentTimestamp;
            },
          };
        }),
        fetchExchange,
      ],
    });
  }

  private appHeader = null;

  public setAppHeader(app: string) {
    this.appHeader = app;
  }
  async geInitialInfo() {
    const response = await this.client.query(GetInitInfo, {}).toPromise();
    return response.data;
  }

  async startChat(patientCase: string, modelver: string): Promise<StartReply> {
    const variables: QueryStartChatArgs = { patientCase, modelver };
    const response = await this.client
      .query(StartChatQuery, variables)
      .toPromise();

    return response.data.startChat;
  }

  async answerQuestionInSession(
    context: any,
    question: string,
    options: QuestionOptions,
    sessionId: string,
  ): Promise<ChatReply> {
    const variables: MutationAskQuestionInSessionArgs = {
      context,
      question,
      options,
      sessionId,
    };
    const response = await this.client
      .mutation(askQuestionInSession, variables)
      .toPromise();
    return response.data.askQuestionInSession;
  }

  async getSessions(showOnlyActive: boolean): Promise<SessionItem[]> {
    const response = await this.client
      .query(getSessionsQuery, { showOnlyActive })
      .toPromise();
    if (response.error) throw new Error(response.error.message);
    return response.data.sessions;
  }

  async getSessionsByUser(
    showOnlyActive: boolean,
    username: string,
  ): Promise<SessionItem[]> {
    const response = await this.client
      .query(getSessionsByUserQuery, { showOnlyActive, username })
      .toPromise();
    if (response.error) throw new Error(response.error.message);
    return response.data.sessionsByUser;
  }

  async startSession(
    patientCase: string,
    modelver: string,
    generateSuggestion: boolean,
  ): Promise<SessionStartReply> {
    const variables = {
      patientCase,
      modelver,
      generateSuggestion,
    };
    const response = await this.client
      .mutation(startSession, variables)
      .toPromise();
    return response.data.startSession;
  }

  async restoreSession(
    sessionId: string,
    allowAnyUser: boolean,
  ): Promise<Session> {
    const variables = { sessionId };
    const response: OperationResult<
      any,
      Exact<{ sessionId?: string }>
    > = await this.client
      .query(!allowAnyUser ? restoreSession : restoreAnySession, variables)
      .toPromise();
    return !allowAnyUser
      ? response.data.restoreSession
      : response.data.restoreAnySession;
  }

  async startSessionByCaseId(
    allowAnyUser: boolean, //only admins can restore sessions of other users
    patientCase: string,
    modelver: string,
    generateSuggestion: boolean,
    caseId: string,
  ): Promise<SessionByCaseIdReply> {
    const variables = { patientCase, modelver, generateSuggestion, caseId };
    const response: OperationResult<any> = await this.client
      .mutation(
        !allowAnyUser
          ? startSessionByCaseIdWithCheck
          : startSessionByCaseIdNoCheck,
        variables,
      )
      .toPromise();
    return !allowAnyUser
      ? response.data.startSessionByCaseIdWithCheck
      : response.data.startSessionByCaseIdNoCheck;
  }

  async removeSession(sessionId: string): Promise<Result> {
    const variables = { sessionId };
    const response = await this.client
      .mutation(removeSession, variables)
      .toPromise();
    return response.data.removeSession;
  }

  async duplicateSession(
    sessionId: string,
    takeover: boolean,
  ): Promise<DuplicateResult> {
    const variables = { sessionId, takeover };
    const response = await this.client
      .mutation(duplicateSession, variables)
      .toPromise();
    return response.data.duplicateSession;
  }

  async updateExchangeFeedback(
    sessionId: string,
    index: number,
    feedback: ExchangeFeedbackInput,
  ): Promise<ExchangeFeedback> {
    const variables = { sessionId, index, feedback };
    const response = await this.client
      .mutation(updateExchangeFeedback, variables)
      .toPromise();
    return response.data.updateExchangeFeedback.feedback;
  }

  async updatePromptFeedback(
    sessionId: string,
    promptName: string,
    predefinedInsight: PredefinedInsight,
    feedback: ExchangeFeedbackInput,
  ): Promise<ExchangeFeedback> {
    const variables = { sessionId, promptName, predefinedInsight, feedback };
    const response = await this.client
      .mutation(updatePromptFeedback, variables)
      .toPromise();
    return response.data.updatePromptFeedback.feedback;
  }

  async userTakeover(sessionId: string): Promise<Result> {
    const variables = { sessionId };
    const response = await this.client
      .mutation(userTakeover, variables)
      .toPromise();
    return response.data.userTakeover;
  }

  //ask a question without creating a new exchange
  async answerQuestion(
    context: any,
    question: string,
    options: QuestionOptions,
  ): Promise<ChatReply> {
    const variables = { context, question, options };
    const response = await this.client
      .query(answerQuestion, variables)
      .toPromise();
    return response.data.answerQuestion;
  }

  async answerPrompt(
    context: any,
    options: QuestionOptions,
    predefinedInsight?: PredefinedInsight,
    prompt?: string,
    promptName?: string,
  ): Promise<PromptReply> {
    const variables = {
      context,
      options,
      predefinedInsight,
      prompt,
      promptName,
    };
    const response = await this.client
      .query(answerPrompt, variables)
      .toPromise();
    if (response.error) throw new Error(response.error.message);
    return response.data.answerPrompt;
  }

  async askPromptInSession(
    context: any,
    options: QuestionOptions,
    predefinedInsight?: PredefinedInsight,
    prompt?: string,
    promptName?: string,
    sessionId?: string,
  ): Promise<PromptReply> {
    const variables: AskPromptInSessionMutationVariables = {
      context,
      options,
      predefinedInsight,
      prompt,
      promptName,
      sessionId,
    };
    const response = await this.client
      .mutation(askPromptInSession, variables)
      .toPromise();
    if (response.error) throw new Error(response.error.message);
    return response.data.askPromptInSession;
  }
}
